import { AlertTriangle, CheckCircle2, Filter, ShieldCheck, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { Card, CardBody } from "@/shared/ui/Card";
import { EmptyState } from "@/shared/ui/EmptyState";
import { HazardLevel } from "@/shared/ui/HazardLevel";
import { Skeleton } from "@/shared/ui/Skeleton";
import { STATE_LABEL_KEY, stateBadge } from "./alertMeta";
import type { Alert, AlertLevel, AlertState } from "@/shared/api/models";

/**
 * FeedCard — the alert list (master) column. Extracted from AlertsPage so the
 * full-screen AlarmCenter overlay can reuse the same master-detail pieces.
 * Selecting a row raises `onSelect(id)`; the parent decides what "selected"
 * means (highlight + detail panel).
 *
 * Rows optionally carry their own 확인/해제 controls (`onAck` / `onClear`), so an
 * operator facing a wall of alarms can retire them **one at a time from the
 * list** instead of having to select → scroll to the detail pane → act. The row
 * body stays a separate button from the actions (no nested <button>), and the
 * actions stop propagation so acting never also re-selects.
 */
export function FeedCard({
  isLoading,
  isError,
  list,
  selectedId,
  onSelect,
  onAck,
  onClear,
  canAck = false,
  canClear = false,
  busyId = null,
  hiddenByFilter = false,
  hiddenCount = 0,
  onShowAll,
}: {
  isLoading: boolean;
  isError: boolean;
  list: Alert[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Per-row acknowledge. Omit (or `canAck=false`) to hide the control. */
  onAck?: (id: number) => void;
  /** Per-row clear. Omit (or `canClear=false`) to hide the control. */
  onClear?: (id: number) => void;
  canAck?: boolean;
  canClear?: boolean;
  /** Alert id with a mutation in flight — its row controls show a spinner. */
  busyId?: number | null;
  /** 받아온 경보는 있는데 현재 필터로 0건인가 — 「경보 없음」과 구분해서 보여준다. */
  hiddenByFilter?: boolean;
  /** 필터 전 전체 건수(안내 문구용). */
  hiddenCount?: number;
  /** 「전체 보기」 버튼 — 필터를 풀어 준다. */
  onShowAll?: () => void;
}) {
  const { t } = useTranslation();
  const showAck = canAck && !!onAck;
  const showClear = canClear && !!onClear;
  const withActions = showAck || showClear;
  return (
    <Card>
      <CardBody className="p-0">
        {isLoading && (
          <div className="p-5 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {isError && (
          <EmptyState
            Icon={AlertTriangle}
            title={t("widgets.alerts.feedCard.errorTitle")}
            description={t("widgets.alerts.feedCard.errorDesc")}
          />
        )}
        {/* ⚠️ 「경보가 없다」와 「필터가 가렸다」는 전혀 다른 상황이다. 예전에는 둘 다
            초록색 「이상 없음」으로 보여서, 하단 탭 배지에 27이 떠 있는데도 화면은
            「정상」이라고 말하는 상태가 됐다 — 안전 시스템에서 가장 위험한 종류의 화면이다. */}
        {hiddenByFilter && (
          <EmptyState
            tone="caution"
            Icon={Filter}
            title={t("widgets.alerts.feedCard.filteredTitle")}
            description={t("widgets.alerts.feedCard.filteredDesc", { n: hiddenCount ?? 0 })}
            action={
              onShowAll ? (
                <Button intent="outline" size="sm" onClick={onShowAll}>
                  {t("widgets.alerts.feedCard.showAll")}
                </Button>
              ) : undefined
            }
          />
        )}
        {!isLoading && !isError && !hiddenByFilter && list.length === 0 && (
          <EmptyState
            tone="success"
            Icon={ShieldCheck}
            title={t("widgets.alerts.feedCard.emptyTitle")}
            description={t("widgets.alerts.feedCard.emptyDesc")}
          />
        )}
        {!isLoading && !isError && list.length > 0 && (
          <ul role="list" className="divide-y divide-[var(--separator)]">
            {list.map((a) => {
              const busy = busyId != null && busyId === a.id;
              return (
                <li
                  key={a.id}
                  /* 좁은 화면에서는 세로 2단. 한 줄에 두면 `shrink-0` 인 액션
                     묶음이 폭을 먼저 가져가고, 남은 자리에서 `min-w-0` 인
                     가스명·메시지가 **폭 0으로 붕괴**해 어떤 경보인지 읽을 수
                     없었다(경보 목록·실경보 오버레이 공통). */
                  className={
                    "flex flex-col lg:flex-row lg:items-center " +
                    "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)] " +
                    (selectedId === a.id ? "bg-[var(--bg-subtle)]" : "hover:bg-[var(--bg-hover)]")
                  }
                >
                  <button
                    type="button"
                    onClick={() => onSelect(a.id!)}
                    className={
                      "min-w-0 w-full lg:w-auto lg:flex-1 text-left py-3.5 pl-4 lg:pl-5 flex items-center gap-3 lg:gap-4 " +
                      (withActions ? "pr-3 lg:pr-3" : "pr-4 lg:pr-5")
                    }
                  >
                    <HazardLevel level={(a.level ?? 0) as AlertLevel} ri={a.ri_at_open} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[14px]">
                        <span className="font-semibold tracking-[-0.005em] truncate">
                          {a.gas || t("widgets.alerts.feedCard.compositeGas")}
                        </span>
                        <Badge tone={stateBadge(a.state as AlertState)} className="shrink-0">
                          {t(STATE_LABEL_KEY[a.state as AlertState])}
                        </Badge>
                      </div>
                      {a.message && (
                        <div className="mt-0.5 text-[12.5px] text-[var(--fg-subtle)] truncate">
                          {a.message}
                        </div>
                      )}
                    </div>
                    <time className="text-[11.5px] text-[var(--fg-subtle)] tabular-nums shrink-0">
                      {/* `hour12:false` 가 없으면 「오후 01:05」로 렌더돼 좁은 폭에서
                          98px 를 먹는다 — 24시간 표기면 절반이다. */}
                      {new Date(a.raised_at!).toLocaleString("ko-KR", {
                        month: "2-digit", day: "2-digit",
                        hour: "2-digit", minute: "2-digit", hour12: false,
                      })}
                    </time>
                  </button>

                  {withActions && (
                    <div className={
                      // 모바일 = 행 아래 풀폭 2분할(터치 44px), 데스크톱 = 기존 우측 배치
                      "flex items-center gap-1.5 " +
                      "w-full px-4 pb-3 [&>*]:flex-1 [&_button]:h-11 " +
                      "lg:w-auto lg:shrink-0 lg:px-0 lg:pl-1 lg:pr-4 lg:pb-0 " +
                      "lg:[&>*]:flex-none lg:[&_button]:h-auto"
                    }>
                      {showAck && (
                        <Button
                          intent="primary"
                          size="xs"
                          loading={busy}
                          disabled={busy || a.state !== "open"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAck!(a.id!);
                          }}
                          leading={<CheckCircle2 size={13} strokeWidth={2} />}
                          title={t("widgets.alerts.feedCard.ackTitle")}
                        >
                          {t("widgets.alerts.feedCard.ack")}
                        </Button>
                      )}
                      {showClear && (
                        <Button
                          intent="outline"
                          size="xs"
                          loading={busy}
                          disabled={busy || a.state === "cleared"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClear!(a.id!);
                          }}
                          leading={<XCircle size={13} strokeWidth={2} />}
                          title={t("widgets.alerts.feedCard.clearTitle")}
                        >
                          {t("widgets.alerts.feedCard.clear")}
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
